import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { toast } from "sonner";
import { EducationForm } from "./EducationForm";
import { DraggableList } from "./DraggableList";
import { BulkActionBar } from "./BulkActionBar";

export const EducationManager = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { data: education } = useQuery({
    queryKey: ["admin-education"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("education")
        .select("*")
        .order("display_order");
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("education").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-education"] });
      queryClient.invalidateQueries({ queryKey: ["education"] });
      toast.success("Education deleted successfully");
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase
        .from("education")
        .delete()
        .in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-education"] });
      queryClient.invalidateQueries({ queryKey: ["education"] });
      setSelectedIds(new Set());
      toast.success("Selected items deleted");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: typeof education) => {
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const { error } = await supabase
          .from("education")
          .update({ display_order: i })
          .eq("id", items[i].id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-education"] });
      queryClient.invalidateQueries({ queryKey: ["education"] });
      toast.success("Order updated");
    },
  });

  const toggleSelection = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const handleReorder = useCallback((newItems: NonNullable<typeof education>) => {
    reorderMutation.mutate(newItems);
  }, [reorderMutation]);

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
        <h2 className="text-2xl font-bold text-foreground mb-6">
          Add Education/Certification
        </h2>
        <EducationForm />
      </Card>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">Existing Education</h3>
        
        {education && education.length > 0 && (
          <BulkActionBar
            selectedCount={selectedIds.size}
            totalCount={education.length}
            onSelectAll={() => setSelectedIds(new Set(education.map((e) => e.id)))}
            onDeselectAll={() => setSelectedIds(new Set())}
            onDeleteSelected={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
            isDeleting={bulkDeleteMutation.isPending}
          />
        )}

        {education && (
          <DraggableList
            items={education}
            onReorder={handleReorder}
            disabled={editingId !== null}
            renderItem={(edu) => (
              <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
                {editingId === edu.id ? (
                  <div className="space-y-4">
                    <EducationForm
                      initialData={edu}
                      onSuccess={() => setEditingId(null)}
                    />
                    <Button
                      variant="outline"
                      onClick={() => setEditingId(null)}
                      className="w-full"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedIds.has(edu.id)}
                      onCheckedChange={() => toggleSelection(edu.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-foreground">
                        {edu.degree_or_certification}
                      </h4>
                      <p className="text-muted-foreground">{edu.institution}</p>
                      {edu.status && (
                        <span className="text-sm text-primary mt-1 inline-block">
                          {edu.status}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => setEditingId(edu.id)}
                      >
                        <Icon name="edit" size={16} />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => deleteMutation.mutate(edu.id)}
                      >
                        <Icon name="delete" size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            )}
          />
        )}
      </div>
    </div>
  );
};
